import { AxiosError } from 'axios';
import clsx from 'clsx';
import { useEffect } from 'react';
import { X } from 'react-feather';
import { useForm } from 'react-hook-form';
import Modal from 'react-modal';
import { useDispatch } from 'react-redux';
import { isIBAN } from 'validator';
import { OrganizationType } from '../enums/organization-type';
import { ProductLabelDimensions } from '../enums/product-label-dimensions';
import { Role } from '../enums/role';
import { HttpExceptionDto } from '../interfaces/http-exception.dto';
import { Organization } from '../interfaces/organization';
import { OrganizationDto } from '../interfaces/organization.dto';
import { User } from '../interfaces/user';
import { api } from '../services/api';
import { organizationTypeToString } from '../services/helpers';
import myToastr from '../services/toastr';
import { getOrganization } from '../store/auth-slice';
import { useAppSelector } from '../store/hooks';
import { RootState } from '../store/store';

interface Props {
  organization: Organization | null;
  show: boolean;
  closeModal: (organization: Organization | null) => void;
}

const OrganizationModal = (props: Props) => {
  const dispatch = useDispatch();
  const user: User = useAppSelector((state: RootState) => state.auth.user!);
  const currentOrganization: Organization = useAppSelector((state: RootState) => state.auth.organization!);
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<OrganizationDto>({
    mode: 'onSubmit',
    defaultValues: {
      name: '',
      cif: '',
      businessName: '',
      address: '',
      phone: '',
      email: '',
      enabledOnlineStores: false,
      invoicePaymentDetail: '',
      url: '',
      postalCode: '',
      city: '',
      province: '',
      country: '',
      type: OrganizationType.B2C,
      productLabelDimensions: ProductLabelDimensions._60x18,
      equivalenceSurcharge: false,
      enabled: true,
      fiskalyEnabled: false,
      requireTransactionPin: true,
      sepaIban: null,
      sepaBic: null,
      sepaCreditorId: null,
    },
  });

  useEffect(() => {
    if (props.show && props.organization) {
      reset({
        name: props.organization.name,
        cif: props.organization.cif,
        businessName: props.organization.businessName,
        address: props.organization.address,
        phone: props.organization.phone,
        email: props.organization.email,
        enabledOnlineStores: props.organization.enabledOnlineStores,
        invoicePaymentDetail: props.organization.invoicePaymentDetail,
        url: props.organization.url,
        postalCode: props.organization.postalCode,
        city: props.organization.city,
        province: props.organization.province,
        country: props.organization.country,
        type: props.organization.type,
        productLabelDimensions: props.organization.productLabelDimensions,
        equivalenceSurcharge: props.organization.equivalenceSurcharge,
        enabled: props.organization.enabled,
        fiskalyEnabled: props.organization.fiskalyEnabled,
        requireTransactionPin: props.organization.requireTransactionPin,
        sepaIban: props.organization.sepaIban,
        sepaBic: props.organization.sepaBic,
        sepaCreditorId: props.organization.sepaCreditorId,
      });
    }
  }, [props.show, props.organization, reset]);

  const close = () => {
    reset();
    props.closeModal(null);
  };

  const onSubmit = async (organizationDto: OrganizationDto) => {
    try {
      organizationDto.sepaIban = organizationDto.sepaIban ? organizationDto.sepaIban.replace(/\s+/g, '') : null;
      if (props.organization) {
        const updatedOrganization = await api.updateOrganization(props.organization.id, organizationDto);
        props.closeModal(updatedOrganization);
        if (props.organization.id === currentOrganization.id) {
          dispatch(getOrganization(currentOrganization.id));
        }
      } else {
        const newOrganization = await api.createOrganization(organizationDto);
        props.closeModal(newOrganization);
      }
      reset();
    } catch (e: any) {
      const axiosError = e as AxiosError;
      if (axiosError?.response) {
        const httpExceptionDto = axiosError.response.data as HttpExceptionDto;
        if (Array.isArray(httpExceptionDto.message)) {
          myToastr.error(httpExceptionDto.message.join('</br>'));
        } else {
          myToastr.error(httpExceptionDto.message);
        }
      }
    }
  };

  return (
    <Modal className="vercomi-modal my-form organization-modal" isOpen={props.show} onRequestClose={close} shouldCloseOnOverlayClick={false}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="content">
          <div className="d-flex justify-content-between mb-3">
            <div className="title">Organización</div>
            <button type="button" className="close-button-modal" onClick={close} title="Cerrar">
              <X size={16} />
            </button>
          </div>
          <div className="row my-3">
            <div className="col-6">
              <div className={'input-name ' + (errors?.type ? 'error' : '')}>Tipo *</div>
              <select {...register('type', { required: true })} className={clsx({ error: errors?.type })} disabled={user.role !== Role.SuperAdmin}>
                <option value={OrganizationType.ALL}>{organizationTypeToString(OrganizationType.ALL)}</option>
                <option value={OrganizationType.B2B}>{organizationTypeToString(OrganizationType.B2B)}</option>
                <option value={OrganizationType.B2C}>{organizationTypeToString(OrganizationType.B2C)}</option>
              </select>
              {errors.type && <div className="error-message">{errors.type.message}</div>}
            </div>
          </div>
          <div className="row my-3">
            <div className="col">
              <div className={'input-name ' + (errors?.name ? 'error' : '')}>Nombre *</div>
              <input type="text" {...register('name', { required: true })} className={clsx({ error: errors?.name })} placeholder="Nombre" />
              {errors.name && <div className="error-message">{errors.name.message}</div>}
            </div>
            <div className="col">
              <div className={clsx('input-name', { error: errors?.businessName })}>Razón Social *</div>
              <input type="text" {...register('businessName', { required: true })} placeholder="Razón social" className={clsx({ error: errors?.businessName })} />
              {errors.businessName && <div className="error-message">{errors.businessName.message}</div>}
            </div>
          </div>
          <div className="row my-3">
            <div className="col">
              <div className={clsx('input-name', { error: errors?.phone })}>Teléfono *</div>
              <input type="number" {...register('phone', { required: true })} placeholder="Teléfono" className={clsx({ error: errors?.phone })} />
              {errors.phone && <div className="error-message">{errors.phone.message}</div>}
            </div>
            <div className="col">
              <div className={clsx('input-name', { error: errors?.cif })}>CIF *</div>
              <input type="text" {...register('cif', { required: true })} className={clsx({ error: errors?.cif })} placeholder="CIF" />
              {errors.cif && <div className="error-message">{errors.cif.message}</div>}
            </div>
          </div>
          <div className="row my-3">
            <div className="col">
              <div className={clsx('input-name', { error: errors?.address })}>Dirección *</div>
              <input type="text" {...register('address', { required: true })} className={clsx({ error: errors?.address })} placeholder="Dirección" />
              {errors.address && <div className="error-message">{errors.address.message}</div>}
            </div>
            <div className="col">
              <div className={clsx('input-name', { error: errors?.email })}>Email *</div>
              <input type="email" {...register('email', { required: true })} className={clsx({ error: errors?.email })} placeholder="Email" />
              {errors.email && <div className="error-message">{errors.email.message}</div>}
            </div>
          </div>
          <div className="row my-3">
            <div className="col-6">
              <div className={clsx('input-name', { error: errors?.postalCode })}>Código postal *</div>
              <input type="text" {...register('postalCode', { required: true })} className={clsx({ error: errors?.postalCode })} placeholder="Código postal" />
              {errors.postalCode && <div className="error-message">{errors.postalCode.message}</div>}
            </div>
            <div className="col-6">
              <div className={clsx('input-name', { error: errors?.city })}>Ciudad *</div>
              <input type="text" {...register('city', { required: true })} className={clsx({ error: errors?.city })} placeholder="Ciudad" />
              {errors.city && <div className="error-message">{errors.city.message}</div>}
            </div>
          </div>
          <div className="row my-3">
            <div className="col-6">
              <div className={clsx('input-name', { error: errors?.province })}>Provincia *</div>
              <input type="text" {...register('province', { required: true })} className={clsx({ error: errors?.province })} placeholder="Provincia" />
              {errors.province && <div className="error-message">{errors.province.message}</div>}
            </div>
            <div className="col-6">
              <div className={clsx('input-name', { error: errors?.country })}>País *</div>
              <input type="text" {...register('country', { required: true })} className={clsx({ error: errors?.country })} placeholder="País" />
              {errors.country && <div className="error-message">{errors.country.message}</div>}
            </div>
          </div>
          <div className="row my-3">
            <div className="col-6">
              <div className={clsx('input-name', { error: errors?.url })}>Url</div>
              <input type="text" {...register('url')} className={clsx({ error: errors?.url })} placeholder="Url" />
              {errors.url && <div className="error-message">{errors.url.message}</div>}
            </div>
            {watch('type') === OrganizationType.B2C && (
              <div className="col-6">
                <div className="input-name">Tiendas Online</div>
                <input type="checkbox" {...register('enabledOnlineStores')} disabled={user.role === Role.Admin || user.role === Role.Manager} />
              </div>
            )}
          </div>
          {user.role === Role.SuperAdmin && (
            <div className="row my-3">
              <div className="col-6">
                <div className="input-name">Dimensiones etiqueta producto</div>
                <select {...register('productLabelDimensions')}>
                  <option value={ProductLabelDimensions._60x18}>{ProductLabelDimensions._60x18}</option>
                  <option value={ProductLabelDimensions._60x18s3}>{ProductLabelDimensions._60x18s3}</option>
                  <option value={ProductLabelDimensions._38x21}>{ProductLabelDimensions._38x21}</option>
                  <option value={ProductLabelDimensions._45x8}>{ProductLabelDimensions._45x8}</option>
                </select>
              </div>
              <div className="col-3">
                <div className="input-name">Activa</div>
                <input type="checkbox" {...register('enabled')} />
              </div>
              <div className="col-3">
                <div className="input-name">Fiskaly</div>
                <input type="checkbox" {...register('fiskalyEnabled')} />
              </div>
            </div>
          )}
          <div className="row my-3">
            <div className="col">
              <div className={clsx('input-name', { error: errors?.invoicePaymentDetail })}>Detalle pago facturas *</div>
              <textarea {...register('invoicePaymentDetail', { required: true })} className={clsx({ error: errors?.invoicePaymentDetail })} placeholder="Texto que aparecerá en el pdf de la factura" />
              {errors.invoicePaymentDetail && <div className="error-message">{errors.invoicePaymentDetail.message}</div>}
            </div>
          </div>
          <div className="row my-3">
            <div className="col-6">
              <div className={'input-name'}>Recargo de equivalencia</div>
              <input type="checkbox" {...register('equivalenceSurcharge')} />
            </div>
            <div className="col-6">
              <div className={clsx('input-name', { error: errors?.sepaIban })}>IBAN usado para emitir remesas SEPA</div>
              <input
                type="text"
                {...register('sepaIban', {
                  validate: (value) => {
                    if (!value) {
                      return true;
                    }
                    return isIBAN(value) || 'Número de cuenta bancaria no válido';
                  },
                })}
                className={clsx({ error: errors?.sepaIban })}
                placeholder="Número de cuenta bancaria"
              />
              {errors.sepaIban && <div className="error-message">{errors.sepaIban.message}</div>}
            </div>
          </div>
          <div className="row my-3">
            <div className="col-6">
              <div className={clsx('input-name', { error: errors?.sepaBic })}>BIC del banco del acreedor para SEPA</div>
              <input type="text" {...register('sepaBic')} className={clsx({ error: errors?.sepaBic })} placeholder="Número BIC" />
              {errors.sepaBic && <div className="error-message">{errors.sepaBic.message}</div>}
            </div>
            <div className="col-6">
              <div className={clsx('input-name', { error: errors?.sepaCreditorId })}>Identificador acreedor SEPA</div>
              <input type="text" {...register('sepaCreditorId')} className={clsx({ error: errors?.sepaCreditorId })} placeholder="Identificador acreedor" />
              {errors.sepaCreditorId && <div className="error-message">{errors.sepaCreditorId.message}</div>}
            </div>
          </div>
          <div className="row">
            <div className="col">
              <div className={'input-name'}>Requiere PIN en transacciones</div>
              <input type="checkbox" {...register('requireTransactionPin')} />
              <div className="mt-1">
                <div style={{ fontSize: 12 }}>
                  Si está activado, se solicitará el <strong>PIN</strong> cada vez para confirmar transacciones.
                </div>
                <div style={{ fontSize: 12 }}>
                  Si no está activado, las transacciones se asignarán <strong>al usuario logueado.</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
        <button className="save-button" type="submit">
          Guardar
        </button>
      </form>
    </Modal>
  );
};

export default OrganizationModal;
